import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Box,
  CircularProgress,
  Link,
  TextField,
  Button,
  Tooltip,
  Chip,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloseIcon from '@mui/icons-material/Close';
import 'react-toastify/dist/ReactToastify.css';
import PropTypes from 'prop-types';
import TimezoneUtils from '../utils/timezoneUtils';
import { EXTERNAL_LINK_REL, stripTrackingParams } from '../utils/externalUrlUtils';

const PAGE_SIZE = 50;
const ALL_RESUMES_VALUE = '__all__';

const getResumeDisplayName = (resume) =>
  resume?.name || resume?.content?.personal_info?.name || 'N/A';

const getProfileLabel = (resume) =>
  resume?.nickname || resume?.content?.personal_info?.name || 'Unnamed resume';

const isAllResumesFilter = (value) =>
  !value || value === ALL_RESUMES_VALUE;

const resolveResumeFilter = (baseResumeId) =>
  baseResumeId ? String(baseResumeId) : ALL_RESUMES_VALUE;

const CustomizedResumes = ({ baseResumeId, dialogMode }) => {
  const [resumes, setResumes] = useState([]);
  const [profileOptions, setProfileOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [urlFilter, setUrlFilter] = useState('');
  const [dateFilter, setDateFilter] = useState(() => TimezoneUtils.getTodayForInput());
  const [resumeFilter, setResumeFilter] = useState(() => resolveResumeFilter(baseResumeId));
  const [globalSearchResults, setGlobalSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/resumes`);
        setProfileOptions(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        setProfileOptions([]);
      }
    };

    fetchProfiles();
  }, []);

  useEffect(() => {
    setResumeFilter(resolveResumeFilter(baseResumeId));
  }, [baseResumeId]);

  const buildDateParams = useCallback((selectedDate) => {
    if (!selectedDate) {
      return {};
    }

    const fromDate = new Date(selectedDate);
    const fromGMT = TimezoneUtils.startOfDayUTC(fromDate);
    const toGMT = TimezoneUtils.endOfDayUTC(fromDate);

    return {
      startDate: fromGMT.toISOString(),
      endDate: toGMT.toISOString(),
    };
  }, []);

  const fetchPage = useCallback(async ({
    offset = 0,
    append = false,
    selectedDate = dateFilter,
    selectedResumeId = resumeFilter,
  } = {}) => {
    const hasDateFilter = Boolean(selectedDate);

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setError('');
    }

    try {
      const params = {
        ...buildDateParams(selectedDate),
      };

      if (!isAllResumesFilter(selectedResumeId)) {
        params.resumeId = selectedResumeId;
      }

      if (!hasDateFilter) {
        params.limit = PAGE_SIZE;
        params.offset = offset;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/resumes/customized/by-date`,
        { params }
      );

      const pageResumes = Array.isArray(response.data.resumes) ? response.data.resumes : [];
      setResumes((prev) => (append ? [...prev, ...pageResumes] : pageResumes));
      setHasMore(hasDateFilter ? false : Boolean(response.data.pagination?.hasMore));
      setTotalCount(response.data.pagination?.total ?? pageResumes.length);
    } catch (err) {
      if (!append) {
        setResumes([]);
        setError('Failed to load customized resumes');
      } else {
        toast.error('Failed to load more customized resumes');
      }
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }, [buildDateParams, dateFilter, resumeFilter]);

  useEffect(() => {
    setGlobalSearchResults([]);
    fetchPage({
      offset: 0,
      append: false,
      selectedDate: dateFilter,
      selectedResumeId: resumeFilter,
    });
  }, [dateFilter, resumeFilter, fetchPage]);

  const filteredResumes = resumes.filter((resume) => {
    const matchesUrl = (resume.url || '').toLowerCase().includes(urlFilter.toLowerCase());
    return matchesUrl;
  });

  const filteredSearchResults = useMemo(() => {
    if (isAllResumesFilter(resumeFilter)) {
      return globalSearchResults;
    }

    return globalSearchResults.filter(
      (resume) => String(resume.resume_id) === String(resumeFilter)
    );
  }, [globalSearchResults, resumeFilter]);

  const handleGlobalSearch = async () => {
    if (!urlFilter.trim()) {
      toast.error('Please enter a URL to search');
      return;
    }

    setIsSearching(true);
    try {
      const params = { url: urlFilter };
      if (!isAllResumesFilter(resumeFilter)) {
        params.resumeId = resumeFilter;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/resumes/customized`,
        { params }
      );
      setGlobalSearchResults(Array.isArray(response.data.resumes) ? response.data.resumes : []);
    } catch (error) {
      setGlobalSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleClearSearch = () => {
    setGlobalSearchResults([]);
    setUrlFilter('');
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if ((e.key === 'Enter' || (e.ctrlKey && e.key === 'Enter')) && urlFilter.trim()) {
        handleGlobalSearch();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [urlFilter]);

  const isSearchMode = globalSearchResults.length > 0;
  const displayedResumes = isSearchMode ? filteredSearchResults : filteredResumes;
  const isDateFiltered = Boolean(dateFilter);
  const selectedProfileLabel = useMemo(() => {
    if (isAllResumesFilter(resumeFilter)) {
      return 'All resumes';
    }

    const profile = profileOptions.find((item) => String(item._id) === String(resumeFilter));
    return profile ? getProfileLabel(profile) : 'All resumes';
  }, [profileOptions, resumeFilter]);

  const selectResumeValue = useMemo(() => {
    if (isAllResumesFilter(resumeFilter)) {
      return ALL_RESUMES_VALUE;
    }

    const normalized = String(resumeFilter);
    const exists = profileOptions.some((item) => String(item._id) === normalized);
    return exists ? normalized : ALL_RESUMES_VALUE;
  }, [resumeFilter, profileOptions]);

  const getSelectDisplayLabel = (value) => {
    if (isAllResumesFilter(value)) {
      return 'All resumes';
    }

    const profile = profileOptions.find((item) => String(item._id) === String(value));
    return profile ? getProfileLabel(profile) : 'All resumes';
  };

  const handleDelete = async (resumeId) => {
    if (!window.confirm('Are you sure you want to delete this resume?')) {
      return;
    }

    setDeletingId(resumeId);
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/resumes/customized/${resumeId}`);
      setResumes((prevResumes) => prevResumes.filter((resume) => resume._id !== resumeId));
      setGlobalSearchResults((prevResults) => prevResults.filter((resume) => resume._id !== resumeId));
      setTotalCount((prev) => Math.max(0, prev - 1));
      toast.success('Resume deleted successfully');
    } catch (error) {
      toast.error('Failed to delete resume');
    } finally {
      setDeletingId(null);
    }
  };

  const handleLoadMore = () => {
    fetchPage({
      offset: resumes.length,
      append: true,
      selectedDate: dateFilter,
      selectedResumeId: resumeFilter,
    });
  };

  if (loading && resumes.length === 0 && profileOptions.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && resumes.length === 0) {
    return (
      <Typography color="error" align="center">
        {error}
      </Typography>
    );
  }

  return (
    <>
      <Box sx={{ pt: dialogMode ? 0 : 4, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="50px">#</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  URL
                  <TextField
                    size="small"
                    placeholder="filter..."
                    value={urlFilter}
                    variant="standard"
                    onChange={(e) => setUrlFilter(e.target.value)}
                    sx={{ ml: 1 }}
                  />
                  <Tooltip title={isSearchMode ? 'Clear Search' : 'Global Search (Enter)'}>
                    <Button
                      variant="outlined"
                      onClick={isSearchMode ? handleClearSearch : handleGlobalSearch}
                      disabled={isSearching}
                      size="small"
                    >
                      {isSearching ? (
                        <CircularProgress size={20} />
                      ) : isSearchMode ? (
                        <CloseIcon sx={{ color: 'red' }} />
                      ) : (
                        <ManageSearchIcon />
                      )}
                    </Button>
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <Select
                    value={selectResumeValue}
                    onChange={(e) => setResumeFilter(e.target.value)}
                    variant="standard"
                    renderValue={getSelectDisplayLabel}
                  >
                    <MenuItem value={ALL_RESUMES_VALUE}>All resumes</MenuItem>
                    {profileOptions.map((profile) => (
                      <MenuItem key={profile._id} value={String(profile._id)}>
                        {getProfileLabel(profile)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell sx={{ display: 'flex', placeItems: 'baseline', alignItems: 'center', gap: 1 }}>
                Date
                <TextField
                  type="date"
                  variant="standard"
                  size="small"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  sx={{ ml: 1, maxWidth: '120px' }}
                />
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : displayedResumes.length > 0 ? (
              displayedResumes.map((resume, index) => (
                <TableRow key={resume._id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Link
                      href={stripTrackingParams(resume.url)}
                      target="_blank"
                      rel={EXTERNAL_LINK_REL}
                    >
                      {resume.url}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {getResumeDisplayName(resume)}
                    {resume.isLegacy && (
                      <Chip
                        label="Legacy profile"
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ ml: 1 }}
                      />
                    )}
                    {resume.nickname && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Nickname: {resume.nickname}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {TimezoneUtils.formatForDisplay(new Date(resume.generated_at), {
                      year: 'numeric',
                      month: 'numeric',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="View PDF">
                        <Button
                          onClick={() => window.open(`${process.env.REACT_APP_API_URL}/resumefiles/${resume.path}`, '_blank')}
                          size="small"
                        >
                          <PictureAsPdfIcon sx={{ mr: 0.2 }} />
                          View
                        </Button>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <Button
                          onClick={() => handleDelete(resume._id)}
                          size="small"
                          color="error"
                          disabled={deletingId === resume._id}
                        >
                          {deletingId === resume._id ? <CircularProgress size={20} /> : <CloseIcon />}
                        </Button>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    No customized resumes found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Box sx={{ mt: 2, display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
          {!isSearchMode && !isDateFiltered && hasMore && (
            <Button variant="outlined" onClick={handleLoadMore} disabled={loadingMore}>
              {loadingMore ? <CircularProgress size={20} /> : 'Load more'}
            </Button>
          )}
          {displayedResumes.length > 0 && (
            <Typography variant="subtitle1" sx={{ ml: 'auto' }}>
              {isSearchMode
                ? `Search results: ${displayedResumes.length}`
                : isDateFiltered
                  ? `${selectedProfileLabel}: ${displayedResumes.length}`
                  : `Showing ${displayedResumes.length}${totalCount ? ` of ${totalCount}` : ''} (${selectedProfileLabel})`}
            </Typography>
          )}
        </Box>
      </Box>
    </>
  );
};

CustomizedResumes.propTypes = {
  baseResumeId: PropTypes.string,
  dialogMode: PropTypes.bool,
};

export default CustomizedResumes;
